import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Minus, TrendingDown, TrendingUp } from "lucide-react";

import { cardSurface } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import {
  addEquipmentZoneLink,
  createEquipmentDependency,
  deleteEquipmentDependencyByPair,
  getAnalytics,
  getBuilding,
  getMeasurementStats,
  removeEquipmentZoneLink,
  updateEquipment,
  type Building,
  type MeasurementStats,
} from "@/lib/api/building";
import {
  fetch_chart_data,
  fmt_ts,
  type ChartData,
  type ChartRow,
} from "@/lib/chart-data";
import {
  detectGaps,
  withGapMarkers,
  staleSinceLabel,
  GAP_FILL,
  GAP_FILL_OPACITY,
} from "@/lib/chart-gaps";
import type { WidgetSpec } from "@/lib/api/agent";
import {
  resolve_multi_trend_metric,
  trendTone,
  valid_trend_channels,
} from "./widget-utils";

// ---------------------------------------------------------------------------
// Shared widget chrome
// ---------------------------------------------------------------------------

function WidgetMessage({ children }: { children: ReactNode }) {
  return (
    <div className="mt-2 flex items-center justify-center rounded-md border border-dashed py-6 text-xs text-muted-foreground">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trend chart widget
// ---------------------------------------------------------------------------

function TrendChartWidget({
  building_id,
  channels,
  days,
  title,
}: Extract<WidgetSpec, { type: "render_trend_chart" }>) {
  const [chart, set_chart] = useState<ChartData | null>(null);
  const [error, set_error] = useState<string | null>(null);
  const valid_channels = useMemo(
    () => valid_trend_channels(channels),
    [channels],
  );

  useEffect(() => {
    if (!building_id) {
      set_chart(null);
      set_error("Kiinteistöä ei valittu kaaviolle.");
      return;
    }
    if (!valid_channels.length) {
      set_chart(null);
      set_error(
        channels.length
          ? `Tuntemattomat signaalit: ${channels.join(", ")}`
          : "Kaavio vaatii vähintään yhden signaalin.",
      );
      return;
    }
    set_error(null);
    fetch_chart_data(building_id, valid_channels, days, title ?? "")
      .then(set_chart)
      .catch((e: unknown) =>
        set_error(e instanceof Error ? e.message : "Virhe"),
      );
  }, [building_id, channels, days, title, valid_channels]);

  if (error) {
    return <p className="text-xs text-destructive mt-1">{error}</p>;
  }

  if (!chart) {
    return <div className={cn(cardSurface, "mt-2 h-[180px] animate-pulse")} />;
  }

  if (chart.rows.length === 0) {
    return <WidgetMessage>Ei dataa valitulle ajanjaksolle</WidgetMessage>;
  }

  const y_axis_ids = [...new Set(chart.channels.map((ch) => ch.yAxisId))];

  // Break the line across real feed outages instead of bridging them: detect
  // over-threshold gaps, then splice null-marker rows so Recharts stops drawing.
  const channel_keys = chart.channels.map((ch) => ch.key);
  const gaps = detectGaps(chart.rows, channel_keys, (r) => r.ts);
  const rows_with_gaps = withGapMarkers(
    chart.rows,
    gaps,
    (markerTs): ChartRow => {
      const row: ChartRow = { ts: markerTs };
      for (const key of channel_keys) row[key] = null;
      return row;
    },
    (r) => r.ts,
  );

  return (
    <div className={cn(cardSurface, "mt-2 p-3")}>
      {title && (
        <p className="mb-2 text-[11px] font-medium text-foreground">{title}</p>
      )}
      <ResponsiveContainer width="100%" height={180}>
        <LineChart
          data={rows_with_gaps}
          margin={{ top: 4, right: 12, left: 0, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis
            dataKey="ts"
            tickFormatter={fmt_ts}
            tick={{ fontSize: 10 }}
            tickLine={false}
          />
          {y_axis_ids.map((yid, i) => {
            const ch = chart.channels.find((c) => c.yAxisId === yid)!;
            return (
              <YAxis
                key={yid}
                yAxisId={yid}
                orientation={i === 0 ? "left" : "right"}
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                unit={` ${ch.unit}`}
                width={48}
              />
            );
          })}
          <Tooltip
            labelFormatter={fmt_ts}
            formatter={(value: number, name: string) => {
              const ch = chart.channels.find((c) => c.key === name);
              return [
                `${value.toFixed(1)} ${ch?.unit ?? ""}`,
                ch?.label ?? name,
              ];
            }}
          />
          <Legend
            formatter={(value: string) => {
              const ch = chart.channels.find((c) => c.key === value);
              return ch?.label ?? value;
            }}
          />
          {gaps.map((gap) => (
            <ReferenceArea
              key={gap.marker}
              yAxisId={y_axis_ids[0]}
              x1={gap.from}
              x2={gap.to}
              fill={GAP_FILL}
              fillOpacity={GAP_FILL_OPACITY}
              ifOverflow="extendDomain"
              label={{
                value: staleSinceLabel(gap.from, fmt_ts),
                position: "insideTop",
                fill: "var(--warning)",
                fontSize: 9,
              }}
            />
          ))}
          {chart.channels.map((ch) => (
            <Line
              key={ch.key}
              dataKey={ch.key}
              yAxisId={ch.yAxisId}
              stroke={ch.color}
              dot={false}
              strokeWidth={2}
              name={ch.key}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Building comparison bar chart widget
// ---------------------------------------------------------------------------

const METRIC_LABEL: Record<string, string> = {
  savings_potential_eur: "Säästöpotentiaali (€/kk)",
  optimization_potential_eur_month: "Säästöpotentiaali (€/kk)",
  active_optimizations: "Aktiiviset optimoinnit",
  energy_kwh_m2: "Energiankulutus (kWh/m²)",
  co2_kg: "CO₂ (kg)",
  optimization_score: "Optimointiaste (%)",
  electricity_kwh: "Sähkönkulutus (kWh)",
  heat_kwh: "Lämmönkulutus (kWh)",
  water_m3: "Vedenkulutus (m³)",
  indoor_temperature: "Keskilämpötila (°C)",
  co2_ppm: "CO₂ (ppm)",
};

const MEASUREMENT_COMPARE_METRICS = new Set([
  "electricity_kwh",
  "heat_kwh",
  "water_m3",
  "indoor_temperature",
  "co2_ppm",
]);

function measurementCompareValue(
  stats: MeasurementStats,
  metric: string,
): number | null {
  const channel = stats[metric as keyof typeof stats];
  if (!channel || typeof channel !== "object" || !("avg" in channel))
    return null;
  const { avg, count } = channel;
  if (count === 0) return null;
  if (metric === "indoor_temperature" || metric === "co2_ppm") return avg;
  return avg * count;
}

function BuildingComparisonWidget({
  metric,
  building_ids,
  days = 7,
}: Extract<WidgetSpec, { type: "render_building_comparison" }>) {
  const [data, set_data] = useState<Array<{
    name: string;
    value: number;
  }> | null>(null);
  const [error, set_error] = useState<string | null>(null);

  useEffect(() => {
    if (MEASUREMENT_COMPARE_METRICS.has(metric)) {
      Promise.all(
        building_ids.map(async (id) => {
          const [building, stats] = await Promise.all([
            getBuilding(id),
            getMeasurementStats(id, days),
          ]);
          return {
            name: building.name,
            value: measurementCompareValue(stats, metric) ?? 0,
          };
        }),
      )
        .then((rows) => {
          rows.sort((a, b) => b.value - a.value);
          set_data(rows);
        })
        .catch((e: unknown) =>
          set_error(e instanceof Error ? e.message : "Virhe"),
        );
      return;
    }

    Promise.all(building_ids.map((id) => getBuilding(id)))
      .then((buildings: Building[]) => {
        const rows = buildings.map((b) => {
          const raw = b[metric as keyof Building];
          return {
            name: b.name,
            value: typeof raw === "number" ? raw : 0,
          };
        });
        rows.sort((a, b) => b.value - a.value);
        set_data(rows);
      })
      .catch((e: unknown) =>
        set_error(e instanceof Error ? e.message : "Virhe"),
      );
  }, [building_ids, metric, days]);

  if (error) return <p className="text-xs text-destructive mt-1">{error}</p>;

  if (!data) {
    return <div className={cn(cardSurface, "mt-2 h-[180px] animate-pulse")} />;
  }

  if (!data.some((row) => row.value > 0)) {
    return (
      <WidgetMessage>Ei vertailudataa valitulle ajanjaksolle</WidgetMessage>
    );
  }

  const label = METRIC_LABEL[metric] ?? metric;
  const BAR_COLOR = "var(--color-primary)";

  return (
    <div className={cn(cardSurface, "mt-2 p-3")}>
      <p className="mb-2 text-[11px] font-medium text-foreground">{label}</p>
      <ResponsiveContainer
        width="100%"
        height={Math.max(120, data.length * 32)}
      >
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 0, right: 16, left: 4, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--border)"
            horizontal={false}
          />
          <XAxis
            type="number"
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            width={100}
          />
          <Tooltip
            formatter={(value: number) => [
              value.toLocaleString("fi-FI"),
              label,
            ]}
          />
          <Bar dataKey="value" radius={[0, 3, 3, 0]}>
            {data.map((_, i) => (
              <Cell
                key={i}
                fill={
                  i === 0
                    ? BAR_COLOR
                    : "color-mix(in oklab, var(--color-primary) 50%, transparent)"
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI summary card widget
// ---------------------------------------------------------------------------

// A trend delta of exactly 0 is neutral (grey, flat dash) — never a green up-arrow (#196).
function TrendBadge({ trend }: { trend: number }) {
  const tone = trendTone(trend);
  const toneClass =
    tone === "up"
      ? "text-success"
      : tone === "down"
        ? "text-destructive"
        : "text-muted-foreground";
  return (
    <span
      className={`flex items-center gap-0.5 text-[10px] font-medium ${toneClass}`}
    >
      {tone === "up" ? (
        <TrendingUp className="h-3 w-3" />
      ) : tone === "down" ? (
        <TrendingDown className="h-3 w-3" />
      ) : (
        <Minus className="h-3 w-3" />
      )}
      {Math.abs(trend).toFixed(1)} %
    </span>
  );
}

function KpiSummaryWidget({
  title,
  items,
}: Extract<WidgetSpec, { type: "render_kpi_summary" }>) {
  if (!items.length) {
    return (
      <WidgetMessage>
        KPI-yhteenvetoa ei voitu näyttää — mittarit puuttuvat.
      </WidgetMessage>
    );
  }

  return (
    <div className={cn(cardSurface, "mt-2 p-3")}>
      <p className="mb-2 text-[11px] font-semibold text-foreground">{title}</p>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-muted-foreground">
              {item.label}
            </span>
            <div className="flex items-center gap-1.5">
              <span className="text-[12px] font-medium tabular-nums text-foreground">
                {item.value.toLocaleString("fi-FI")} {item.unit}
              </span>
              {item.trend !== undefined && <TrendBadge trend={item.trend} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Multi-building trend chart widget
// ---------------------------------------------------------------------------

const _PALETTE = [
  "var(--color-primary)",
  "#10b981",
  "#f59e0b",
  "#6366f1",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#f97316",
];

type _ChartRow = Record<string, number | string | null>;

function MultiTrendChartWidget({
  building_ids,
  metric,
  days,
  title,
}: Extract<WidgetSpec, { type: "render_multi_building_trend" }>) {
  const [rows, set_rows] = useState<_ChartRow[]>([]);
  const [names, set_names] = useState<string[]>([]);
  const [error, set_error] = useState<string | null>(null);
  const [loading, set_loading] = useState(true);
  const { effective_metric, meta } = resolve_multi_trend_metric(metric);

  useEffect(() => {
    // Re-resolve from the raw `metric` prop rather than closing over the outer `meta`/
    // `effective_metric` above — those are new object/string values every render, which would
    // otherwise force this effect to re-run every render if listed as dependencies.
    const { effective_metric: resolved_metric, meta: resolved_meta } =
      resolve_multi_trend_metric(metric);
    if (!resolved_meta) {
      set_error(`Tuntematon mittari: ${resolved_metric}`);
      set_loading(false);
      return;
    }
    Promise.all([
      Promise.all(building_ids.map((id) => getBuilding(id))),
      Promise.all(building_ids.map((id) => getAnalytics(id, days))),
    ])
      .then(([buildings, analytics]) => {
        const bldNames = buildings.map((b) => b.name);
        set_names(bldNames);
        const hourMap = new Map<string, _ChartRow>();
        analytics.forEach((anal, i) => {
          const name = bldNames[i];
          anal.hourly_series.forEach((sample) => {
            if (!hourMap.has(sample.hour)) {
              hourMap.set(sample.hour, { hour: sample.hour });
            }
            const row = hourMap.get(sample.hour)!;
            row[name] = sample[resolved_meta.key];
          });
        });
        const merged = [...hourMap.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([, row]) => row);
        set_rows(merged);
        set_loading(false);
      })
      .catch((e: unknown) => {
        set_error(e instanceof Error ? e.message : "Virhe");
        set_loading(false);
      });
  }, [building_ids, metric, days]);

  if (error) return <p className="text-xs text-destructive mt-1">{error}</p>;

  if (loading) {
    return <div className={cn(cardSurface, "mt-2 h-[200px] animate-pulse")} />;
  }

  if (!rows.length) {
    return (
      <div className="mt-2 flex items-center justify-center rounded-md border border-dashed py-6 text-xs text-muted-foreground">
        Ei dataa valitulle ajanjaksolle
      </div>
    );
  }

  const fmt_hour = (iso: string) =>
    typeof iso === "string" ? iso.slice(5, 16).replace("T", " ") : String(iso);

  // Break each building's line across real outages rather than bridging them.
  const get_hour = (r: _ChartRow) => r.hour as string;
  const gaps = detectGaps(rows, [effective_metric], get_hour);
  const rows_with_gaps = withGapMarkers(
    rows,
    gaps,
    (markerTs): _ChartRow => {
      const row: _ChartRow = { hour: markerTs };
      for (const name of names) row[name] = null;
      return row;
    },
    get_hour,
  );

  return (
    <div className={cn(cardSurface, "mt-2 p-3")}>
      {title && (
        <p className="mb-2 text-[11px] font-medium text-foreground">{title}</p>
      )}
      <ResponsiveContainer width="100%" height={200}>
        <LineChart
          data={rows_with_gaps}
          margin={{ top: 4, right: 12, left: 0, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis
            dataKey="hour"
            tickFormatter={fmt_hour}
            tick={{ fontSize: 10 }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            unit={` ${meta?.unit ?? ""}`}
            width={52}
          />
          <Tooltip
            labelFormatter={fmt_hour}
            formatter={(value: number, name: string) => [
              `${value.toFixed(1)} ${meta?.unit ?? ""}`,
              name,
            ]}
          />
          <Legend />
          {gaps.map((gap) => (
            <ReferenceArea
              key={gap.marker}
              x1={gap.from}
              x2={gap.to}
              fill={GAP_FILL}
              fillOpacity={GAP_FILL_OPACITY}
              ifOverflow="extendDomain"
              label={{
                value: staleSinceLabel(gap.from, fmt_hour),
                position: "insideTop",
                fill: "var(--warning)",
                fontSize: 9,
              }}
            />
          ))}
          {names.map((name, i) => (
            <Line
              key={name}
              dataKey={name}
              stroke={_PALETTE[i % _PALETTE.length]}
              dot={false}
              strokeWidth={2}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Config action proposal widget
// ---------------------------------------------------------------------------

type ActionStatus = "pending" | "loading" | "done" | "cancelled" | "error";

const KIND_LABEL: Record<string, string> = {
  add_zone_link: "Yhdistä vyöhykkeeseen",
  remove_zone_link: "Poista vyöhykelinkki",
  add_dependency: "Lisää riippuvuus",
  remove_dependency: "Poista riippuvuus",
  set_variants: "Muuta toimintoja (variantit)",
};

function ConfigActionWidget({
  widget,
  buildingId,
}: {
  widget: Extract<WidgetSpec, { type: "render_config_action" }>;
  buildingId: string;
}) {
  const [status, set_status] = useState<ActionStatus>("pending");

  async function handle_confirm() {
    set_status("loading");
    try {
      const { kind } = widget;
      if (kind === "add_zone_link" && widget.equipment_id && widget.zone_id) {
        await addEquipmentZoneLink(
          buildingId,
          widget.equipment_id,
          widget.zone_id,
        );
      } else if (
        kind === "remove_zone_link" &&
        widget.equipment_id &&
        widget.zone_id
      ) {
        await removeEquipmentZoneLink(
          buildingId,
          widget.equipment_id,
          widget.zone_id,
        );
      } else if (
        kind === "add_dependency" &&
        widget.source_equipment_id &&
        widget.target_equipment_id
      ) {
        await createEquipmentDependency(buildingId, {
          source_equipment_id: widget.source_equipment_id,
          target_equipment_id: widget.target_equipment_id,
          dependency_type: widget.dependency_type ?? "feeds",
        });
      } else if (
        kind === "remove_dependency" &&
        widget.source_equipment_id &&
        widget.target_equipment_id
      ) {
        await deleteEquipmentDependencyByPair(
          buildingId,
          widget.source_equipment_id,
          widget.target_equipment_id,
        );
      } else if (
        kind === "set_variants" &&
        widget.equipment_id &&
        widget.declared_variants
      ) {
        // The equipment's active_behaviors re-derives from declared_variants on the next
        // read — no dedicated activation endpoint (post-#190 variant-derived model).
        await updateEquipment(buildingId, widget.equipment_id, {
          declared_variants: widget.declared_variants,
        });
      }
      set_status("done");
    } catch {
      set_status("error");
    }
  }

  const label = KIND_LABEL[widget.kind] ?? widget.kind;

  return (
    <div className="mt-2 rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2">
      <p className="text-[11px] font-semibold text-primary">{label}</p>
      <p className="text-xs text-foreground">{widget.rationale_fi}</p>
      {widget.kind === "set_variants" && widget.declared_variants && (
        <p className="text-[11px] text-muted-foreground">
          Uudet variantit:{" "}
          {widget.declared_variants.length > 0
            ? widget.declared_variants.join(", ")
            : "(ei yhtään)"}
        </p>
      )}
      {status === "pending" && (
        <div className="flex gap-2">
          <button
            onClick={() => void handle_confirm()}
            className="rounded bg-primary px-3 py-1.5 text-xs text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Vahvista
          </button>
          <button
            onClick={() => set_status("cancelled")}
            className="rounded border border-border/60 px-3 py-1.5 text-xs text-muted-foreground hover:border-border hover:text-foreground transition-colors"
          >
            Peruuta
          </button>
        </div>
      )}
      {status === "loading" && (
        <p className="text-xs text-muted-foreground">Käsitellään…</p>
      )}
      {status === "done" && (
        <p className="text-xs text-success-subtle-foreground">Valmis</p>
      )}
      {status === "cancelled" && (
        <p className="text-xs text-muted-foreground">Peruutettu</p>
      )}
      {status === "error" && (
        <p className="text-xs text-destructive">Virhe — yritä uudelleen</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-level dispatcher
// ---------------------------------------------------------------------------

interface Props {
  widget: WidgetSpec;
  buildingId?: string;
}

export function WidgetRenderer({ widget, buildingId }: Props) {
  switch (widget.type) {
    case "render_trend_chart":
      return (
        <TrendChartWidget
          {...widget}
          building_id={widget.building_id || buildingId || ""}
        />
      );
    case "render_building_comparison":
      return <BuildingComparisonWidget {...widget} />;
    case "render_kpi_summary":
      return <KpiSummaryWidget {...widget} />;
    case "render_multi_building_trend":
      return <MultiTrendChartWidget {...widget} />;
    case "render_config_action":
      if (!buildingId) return null;
      return <ConfigActionWidget widget={widget} buildingId={buildingId} />;
    default:
      // An unrecognised widget type (e.g. the backend ships a new one before the frontend
      // knows it) must render a safe fallback — returning undefined crashes the chat bubble
      // ("Nothing was returned from render"), and there is no error boundary here.
      return (
        <p className="mt-2 text-xs text-muted-foreground">
          Tuntematon widget-tyyppi.
        </p>
      );
  }
}
